import React, { useEffect, useState } from 'react';
import './SpecialEventPayment.scss';
import { http, FxSelect, FxCurrency, AlertService, localstorage } from '../../fx-core';
import { Button } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import * as _ from 'lodash';

// ── QR code image URL ─────────────────────────────────────────────────────────
const QR_CODE_IMAGE_URL = "https://d11w9j7qb25q56.cloudfront.net/test-uploads/LocalitesQRcode.png";

const SpecialEventPayment = (props) => {
    const defaultItem = {
        paymentModeId: -1,
        amount: props.amount,
        id: props.id
    };

    const [paymentMode, setPaymentMode] = useState([]);
    const [item, setItem] = useState(defaultItem);
    const [couponCode, setCouponCode] = useState("");
    const [couponData, setCouponData] = useState(null);
    const [finalAmount, setFinalAmount] = useState(props.amount);
    const [existingPaymentData, setExistingPaymentData] = useState(null);

    // ------------ LOAD EXISTING PAYMENT DATA -------------
    const loadExistingPaymentData = () => {
        http.get({
            url: `payment/servicerequest/${item.id}`
        }).then(res => {
            const existingData = res.data;
            setExistingPaymentData(existingData);

            if (existingData.discountAmount > 0 && existingData.couponCode) {
                setCouponCode(existingData.couponCode);
                const couponDataObj = {
                    originalAmount: existingData.originalAmount || props.amount,
                    discountAmount: existingData.discountAmount,
                    remainingAmount: existingData.amount
                };
                setCouponData(couponDataObj);
                setFinalAmount(existingData.amount);
                setItem(prev => ({ ...prev, amount: existingData.amount }));
            } else {
                setFinalAmount(props.amount);
                setItem(prev => ({ ...prev, amount: props.amount }));
            }
        }).catch(error => {
            console.log("No existing payment data found, using default");
        });
    };

    // ---------------- APPLY COUPON ----------------
    const applyCoupon = () => {
        if (!couponCode) {
            AlertService.showErrorMsg("Enter coupon code");
            return;
        }

        const inputData = {
            couponCode: couponCode,
            amount: props.amount,
            FeatureCouponType: 6
        };

        http.post({ url: "coupon/validate", data: inputData })
            .then(res => {
                const data = res.data;
                let payable = data.remainingAmount;
                let discountValue = data.discountAmount;
                let originalAmount = props.amount;

                if (data.couponType === "Percent") {
                    const actualDiscount = (originalAmount * discountValue) / 100;
                    payable = originalAmount - actualDiscount;
                    data.discountAmount = actualDiscount;
                    data.originalAmount = originalAmount;
                } else if (data.couponType === "Fixed") {
                    payable = originalAmount - discountValue;
                    data.originalAmount = originalAmount;
                }

                setCouponData(data);
                setFinalAmount(payable);
                setItem(prev => ({ ...prev, amount: payable }));
                AlertService.showSuccessMsg("Coupon applied!");

                if (payable === 0) {
                    autoSubmitFullCoupon(data.originalAmount || props.amount, data.discountAmount || props.amount);
                }
            })
            .catch(() => {
                AlertService.showErrorMsg("Invalid coupon");
            });
    };

    // ------------ AUTO SUBMIT FOR FULL COUPON -------------
    const autoSubmitFullCoupon = (originalAmt, discountAmt) => {
        let inputData = {
            entityId: item.id,
            entityTypeId: 6,
            paymentMode: "coupon",
            amount: 0,
            originalAmount: existingPaymentData?.discountAmount > 0
                ? existingPaymentData.originalAmount
                : originalAmt,
            discountAmount: discountAmt,
            couponCode: couponCode
        };

        http.put({ url: `payment/servicerequest/paymentmode`, data: inputData }).then(res => {
            AlertService.showSuccessMsg("Coupon applied successfully!");
            setTimeout(() => {
                props.handleClose({
                    id: item.id,
                    paymentMode: "coupon",
                    amount: 0,
                    originalAmount: inputData.originalAmount,
                    discountAmount: discountAmt,
                    isFullCoupon: true
                });
            }, 2000);
        }).catch(error => {
            AlertService.showErrorMsg("Failed to apply coupon");
        });
    };

    // ------------ REMOVE COUPON -------------
    const removeCoupon = () => {
        setCouponCode("");
        setCouponData(null);
        setFinalAmount(props.amount);
        setItem(prev => ({ ...prev, amount: props.amount, paymentModeId: -1 }));
    };

    // ------------ LOAD PAYMENT MODES -------------
    const initLookup = () => {
        http.post({
            url: "options/lookup",
            data: { lookups: { PaymentMode: { default: true } }, filters: {} }
        }).then(response => {
            let list = response.data.PaymentMode;
            list = list.filter(x => x.id !== -1);
            list = list.filter(x => x.code !== "coupon");
            list = list.map(x => x.text === "Credit/Debit Card" ? { ...x, text: "Cash / UPI" } : x);
            setPaymentMode(list);
        });
    };

    // ── 🔔 PAYMENT NOTIFICATION ───────────────────────────────────────────────
    // Notifies all Special Community Admins of the community when a payment
    // is submitted (Bank Transfer or QR/UPI). Fire-and-forget — never blocks UI.
    const sendPaymentNotification = () => {
        try {
            http.post({
                url: 'specialevent/notify',
                data: {
                    notificationType: 'payment_submitted',
                    communityId: props.communityId,
                    communityName: props.communityName,
                    stallName: props.stallName,
                    amount: finalAmount,
                    fromDate: props.startDate,
                    toDate: props.endDate,
                    createdBy: localstorage.getItem('userId')
                }
            });
        } catch (e) {
            // Notification failure must never block the payment flow
            console.error('Payment notification failed', e);
        }
    };

    // ---------- BANK TRANSFER ----------
    const bankTransfer = () => {
        const originalAmt = couponData?.originalAmount || props.amount;
        const discountAmt = couponData?.discountAmount || 0;

        let inputData = {
            entityId: item.id,
            entityTypeId: 6,
            paymentMode: "accounttransfer",
            amount: finalAmount,
            originalAmount: existingPaymentData?.originalAmount > 0
                ? existingPaymentData.originalAmount
                : originalAmt,
            discountAmount: discountAmt,
            couponCode: couponData ? couponCode : null
        };

        http.put({ url: `payment/servicerequest/paymentmode`, data: inputData }).then(res => {
            AlertService.showSuccessMsg("Bank Transfer submitted!");
            sendPaymentNotification(); // 🔔 notify Special Community Admins
            props.handleClose({
                id: item.id,
                paymentMode: "accounttransfer",
                amount: finalAmount,
                originalAmount: inputData.originalAmount,
                discountAmount: discountAmt
            });
        }).catch(error => {
            AlertService.showErrorMsg("Bank transfer submission failed");
        });
    };

    // ---------- QR / UPI TRANSFER (mirrors bank transfer flow) ----------
    const qrTransfer = () => {
        const originalAmt = couponData?.originalAmount || props.amount;
        const discountAmt = couponData?.discountAmount || 0;

        let inputData = {
            entityId: item.id,
            entityTypeId: 6,
            paymentMode: "accounttransfer",   // same mode as bank transfer
            amount: finalAmount,
            originalAmount: existingPaymentData?.originalAmount > 0
                ? existingPaymentData.originalAmount
                : originalAmt,
            discountAmount: discountAmt,
            couponCode: couponData ? couponCode : null
        };

        http.put({ url: `payment/servicerequest/paymentmode`, data: inputData }).then(res => {
            AlertService.showSuccessMsg("Payment submitted!");
            sendPaymentNotification(); // 🔔 notify Special Community Admins
            props.handleClose({
                id: item.id,
                paymentMode: "accounttransfer",
                amount: finalAmount,
                originalAmount: inputData.originalAmount,
                discountAmount: discountAmt
            });
        }).catch(error => {
            AlertService.showErrorMsg("Payment submission failed");
        });
    };

    // ---------- FINAL SUBMIT ----------
    const handleSubmit = () => {
        if (item.paymentModeId === -1) {
            AlertService.showErrorMsg("Select a payment mode");
            return;
        }

        if (item.paymentModeId === 2) {
            // Bank transfer
            bankTransfer();
        } else if (isQrMode) {
            // QR / Cash+Card — same submit flow as bank transfer
            qrTransfer();
        }
        /* ── Razorpay flow — commented out, replaced by QR ──────────────────
        else {
            updatePaymentMode();
        }
        ────────────────────────────────────────────────────────────────────── */
    };

    // ---------- UPDATE PAYMENT MODE (Razorpay) — kept but commented out ----------
    /* ── Razorpay updatePaymentMode ────────────────────────────────────────────
    const updatePaymentMode = () => {
        if (item.paymentModeId == -1) {
            AlertService.showErrorMsg("Please select payment mode.");
            return;
        }

        let selPayMode = _.find(paymentMode, { id: item.paymentModeId });
        const originalAmt = couponData?.originalAmount || props.amount;
        const discountAmt = couponData?.discountAmount || 0;

        let inputData = {
            entityId: item.id,
            entityTypeId: 6,
            paymentMode: selPayMode.code,
            amount: finalAmount,
            originalAmount: existingPaymentData?.originalAmount > 0
                ? existingPaymentData.originalAmount
                : originalAmt,
            discountAmount: discountAmt,
            couponCode: couponData ? couponCode : null
        };

        http.put({
            url: `payment/servicerequest/paymentmode`,
            data: inputData
        }).then(res => {
            props.handleClose({
                id: item.id,
                paymentMode: selPayMode.code,
                amount: finalAmount,
                originalAmount: inputData.originalAmount,
                discountAmount: discountAmt
            });
        }).catch(error => {
            AlertService.showErrorMsg("Payment update failed");
        });
    };
    ────────────────────────────────────────────────────────────────────────── */

    // Helper: is the selected mode a QR/Cash+Card mode (i.e. not bank transfer, not unselected)
    const selectedModeCode = _.find(paymentMode, { id: item.paymentModeId })?.code ?? "";
    const isQrMode = item.paymentModeId !== -1 && item.paymentModeId !== 2;

    // Auto-select bank transfer only if amount = 0
    useEffect(() => {
        if (finalAmount == 0) {
            setItem(prev => ({ ...prev, paymentModeId: 2 }));
        }
    }, [finalAmount]);

    useEffect(() => {
        initLookup();
        loadExistingPaymentData();
    }, []);

    return (
        <div>
            {/* HEADER */}
            <div className="row justify-content-between title-font py-1 mx-auto px-3">
                <div className="py-1">
                    <span>Payment</span>
                </div>
                <div className="float-right pointer" onClick={() => props.handleClose()}>
                    <HighlightOffIcon fontSize="small" color="secondary" />
                </div>
            </div>

            <div className="m-4">
                {/* AMOUNT */}
                <div className='m-1 row'>
                    <span className='font-weight-bold'>Amount</span>
                    <span className='ml-3'>
                        <FxCurrency value={finalAmount} />
                    </span>
                    {couponData && (
                        <div className="col-12 mt-1">
                            <small>
                                Original: <FxCurrency value={couponData.originalAmount || props.amount} />
                                &nbsp;| Discount: -<FxCurrency value={couponData.discountAmount || 0} />
                            </small>
                        </div>
                    )}
                </div>

                {/* COUPON */}
                <div className="m-1 pt-2">
                    <label className='font-weight-bold'>Coupon</label>
                    <div className="row">
                        <div className="col-8">
                            <input
                                type="text"
                                className="form-control"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                placeholder="Enter coupon"
                                disabled={couponData !== null}
                            />
                        </div>
                        <div className="col-4">
                            {!couponData &&
                                <Button color="primary" variant="contained" onClick={applyCoupon}>Apply</Button>
                            }
                            {couponData &&
                                <Button color="secondary" variant="contained" onClick={removeCoupon}>Remove</Button>
                            }
                        </div>
                    </div>
                    {couponData && (
                        <div className="mt-2">
                            Coupon Applied: -<FxCurrency value={couponData.discountAmount || 0} />
                        </div>
                    )}
                </div>

                {/* PAYMENT MODE - Only show if amount > 0 */}
                {finalAmount > 0 && (
                    <>
                        <div className="m-1 pt-3">
                            <FxSelect
                                name="paymentModeId"
                                variant="outlined"
                                label="Payment Mode"
                                options={paymentMode}
                                selectedValue={item.paymentModeId}
                                valueField="id"
                                displayField="text"
                                fullWidth
                                size="small"
                                onValueChange={(e) => setItem(prev => ({ ...prev, paymentModeId: e.value }))}
                            />
                        </div>

                        {/* BANK DETAILS — shown for bank transfer */}
                        {item.paymentModeId == 2 && (
                            <div className='m-1 pt-3'>
                                <div>Company name : AUTHY HYPERLOCAL PRIVATE LIMITED</div>
                                <div>A/C No : 66661236660</div>
                                <div>IFSC : IDFB0080531</div>
                                <div>Bank name: IDFC FIRST</div>
                                <div>Branch : Coimbatore Branch</div>
                                <div className='mt-2 text-danger'>
                                    Kindly transfer the amount and then submit. Request to be approved shortly.
                                </div>
                            </div>
                        )}

                        {/* QR CODE — shown for Cash/Card (non-bank, non-unselected) */}
                        {/* ── Razorpay redirect was here; replaced with QR display ── */}
                        {isQrMode && (
                            <div className='spl-qr-payment m-1 pt-3'>
                                <div className='spl-qr-payment__instruction'>
                                    Scan the QR code below using any UPI app to pay
                                </div>
                                <div className='spl-qr-payment__amount'>
                                    ₹{finalAmount}
                                </div>
                                <div className='spl-qr-payment__image-wrap'>
                                    <img
                                        src={QR_CODE_IMAGE_URL}
                                        alt="UPI QR Code"
                                        className='spl-qr-payment__image'
                                    />
                                </div>
                                <div className='spl-qr-payment__note'>
                                    After scanning and paying, click <b>Submit</b> to confirm your enrollment.
                                </div>
                            </div>
                        )}

                        {/* SUBMIT */}
                        <div className="m-1 pt-3 text-center">
                            <Button variant="contained" size="medium" color="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SpecialEventPayment;